import { Skeleton } from 'antd'
import SectionTitle from 'components/SectionTitle'
import { timeAgoStr, withinDays } from 'lib/date'
import { createViewAnswerLink } from 'lib/link'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useParams } from 'react-router-dom'
import {
  useBookQuery,
  useBookQuestionsQuery,
  useBookRoleQuery,
  useBookUsersQuery,
} from 'store/queries'
import { RecentActivityContainer } from './style'

function RecentActivity() {
  const { t } = useTranslation()
  const { bookId } = useParams()
  const history = useHistory()
  const { data: book } = useBookQuery(bookId)
  const { data: questions } = useBookQuestionsQuery(bookId)
  const { data: users } = useBookUsersQuery(bookId)
  const { data: role } = useBookRoleQuery(bookId)

  const isEditorOrPurchaser =
    role?.name === 'Purchaser' || role?.name === 'Editor'

  const recentActivity = []
  users
    .filter(
      (u) =>
        !u.is_retired &&
        u.cognito_id &&
        withinDays(u.last_modified_datetime, 1),
    )
    .forEach((u) => {
      recentActivity.push({
        type: 'user-added',
        first_name: u.first_name,
        last_name: u.last_name,
        role: u.name,
        dsply_text: '',
        activity_time: u.last_modified_datetime,
        dsply_time: timeAgoStr(t, u.last_modified_datetime),
      })
    })

  questions
    .filter(
      (q) =>
        q.is_sent &&
        q.sent_datetime &&
        isEditorOrPurchaser &&
        withinDays(q.sent_datetime, 1),
    )
    .forEach((q) => {
      recentActivity.push({
        type: 'prompt-added',
        book_question_join_id: q.book_question_join_id,
        first_name: '',
        last_name: '',
        role: '',
        dsply_text: q.dsply_text,
        activity_time: q.sent_datetime,
        dsply_time: timeAgoStr(t, q.sent_datetime),
      })
    })

  questions.forEach((q) =>
    q.answers
      .filter(
        (a) =>
          a.is_submitted &&
          a.allow_publish &&
          withinDays(a.is_submitted_datetime, 1),
      )
      .forEach((a) => {
        recentActivity.push({
          type: 'answer-added',
          answer_id: a.id,
          first_name: a.first_name,
          last_name: a.last_name,
          role: '',
          dsply_text: q.dsply_text,
          activity_time: a.is_submitted_datetime,
          dsply_time: timeAgoStr(t, a.is_submitted_datetime),
        })
      }),
  )

  recentActivity.sort((t1, t2) =>
    t1.activity_time > t2.activity_time ? 1 : -1,
  )

  const handleSharedAnswerClick = (activity) => {
    // We don't have the proper answer data in recent activity, so we need to grab it.
    let question
    let answer
    questions?.forEach((q) =>
      q.answers.forEach((a) => {
        if (a.id === activity.answer_id) {
          question = q
          answer = a
        }
      }),
    )
    if (!question) {
      throw new Error('Question not found')
    }
    if (!answer) {
      throw new Error('Answer not found')
    }

    history.push(createViewAnswerLink(bookId, question.id, answer.id))
  }

  const handleNewPromptActivityClick = () => {
    history.push(`/dashboard/book/${bookId}/stories/view/all`)
  }

  return (
    <RecentActivityContainer>
      <div className="section-container">
        {book.id ? (
          <>
            <div className="bb-line p20 title-container">
              <SectionTitle>{t('recent_activity')}</SectionTitle>
            </div>

            <div className="activity-group">
              {!recentActivity.length && <div>No recent activity</div>}
              {recentActivity.map((activity, i) => {
                if (activity.type === 'user-added') {
                  return (
                    <div key={i} className="bb-line p20201040 cursor">
                      <Link to={`/dashboard/book/${bookId}/friends/view`}>
                        <div className="mb-1">
                          <b>
                            {activity.first_name} {activity.last_name}
                          </b>
                          ({activity.role}) {t('has_joined')}
                        </div>
                        <div className="dsply-time">{activity.dsply_time}</div>
                      </Link>
                    </div>
                  )
                } else if (activity.type === 'answer-added') {
                  // User shared answer.
                  return (
                    <div
                      key={i}
                      className="bb-line p20201040 cursor"
                      onClick={() => {
                        handleSharedAnswerClick(activity)
                      }}
                    >
                      <div className="mb-1">
                        <b>
                          {activity.first_name} {activity.last_name}
                        </b>{' '}
                        {t('shared')} &ldquo;{activity.dsply_text}&rdquo;
                      </div>
                      <div className="dsply-time">{activity.dsply_time}</div>
                    </div>
                  )
                } else {
                  // New story prompt.
                  return (
                    <div
                      key={i}
                      className="bb-line p20201040 cursor"
                      onClick={() => {
                        handleNewPromptActivityClick(activity)
                      }}
                    >
                      <div className="mb-1">
                        {t('new_story_prompt')} &ldquo;{activity.dsply_text}
                        &rdquo;
                      </div>
                      <div className="dsply-time">{activity.dsply_time}</div>
                    </div>
                  )
                }
              })}
            </div>
          </>
        ) : (
          <Skeleton active paragraph={{ rows: 4 }} />
        )}
      </div>
    </RecentActivityContainer>
  )
}

export default RecentActivity
